import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AFTER_SALES_STATUS_TEXT,
  AfterSalesRequest,
  AfterSalesStatus
} from '../types/afterSales';
import '../pages/MemberManagement.css';

const { Paragraph } = Typography;

const STATUS_COLOR: Partial<Record<AfterSalesStatus, string>> = {
  pending_merchant_review: 'orange',
  pending_team_price_confirmation: 'gold',
  pending_user_return: 'blue',
  pending_merchant_fulfillment: 'purple',
  pending_team_completion: 'cyan',
  completed: 'green',
  merchant_rejected: 'red'
};

async function merchantFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem('token');
  const response = await fetch(`/api/merchant${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {})
    }
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.error || '请求失败');
  return result;
}

const itemLabel = (item: Record<string, unknown>) =>
  String(item.name || item.cas || item.model || '商品');

const MerchantAfterSales: React.FC = () => {
  const [rows, setRows] = useState<AfterSalesRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AfterSalesRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await merchantFetch('/after-sales');
      setRows(result.data || []);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载售后失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const review = async (approved: boolean) => {
    if (!selected) return;
    try {
      const values = await form.validateFields(
        approved
          ? [selected.type === 'return' ? 'merchantConfirmedAmount' : 'replacementQuote']
          : ['note']
      );
      if (!approved && !String(values.note || '').trim()) {
        message.warning('请填写拒绝原因');
        return;
      }
      setSubmitting(true);
      await merchantFetch(`/after-sales/${selected._id}/review`, {
        method: 'PUT',
        body: JSON.stringify({ approved, ...values })
      });
      message.success(approved ? '审核结果和报价已提交' : '已拒绝售后申请');
      setSelected(null);
      form.resetFields();
      await load();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fulfill = async () => {
    if (!selected) return;
    try {
      const values = await form.validateFields([
        selected.type === 'return' ? 'refundReference' : 'trackingNumber'
      ]);
      setSubmitting(true);
      await merchantFetch(`/after-sales/${selected._id}/fulfill`, {
        method: 'PUT',
        body: JSON.stringify(values)
      });
      message.success(selected.type === 'return' ? '已标记退款完成' : '换货发货信息已提交');
      setSelected(null);
      form.resetFields();
      await load();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ColumnsType<AfterSalesRequest> = [
    {
      title: '商品',
      dataIndex: 'originalItem',
      render: (item) => itemLabel(item)
    },
    { title: '申请人', dataIndex: 'applicantName' },
    { title: '类型', dataIndex: 'type', render: (value) => value === 'return' ? '退货退款' : '换货' },
    { title: '数量', dataIndex: 'requestedQuantity' },
    {
      title: '原商品售后价值',
      dataIndex: 'originalReturnValue',
      render: (value) => `¥${Number(value || 0).toFixed(2)}`
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value: AfterSalesStatus) => (
        <Tag color={STATUS_COLOR[value]}>{AFTER_SALES_STATUS_TEXT[value]}</Tag>
      )
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      render: (value) => new Date(value).toLocaleString()
    },
    {
      title: '操作',
      render: (_, row) => (
        <Button
          type={['pending_merchant_review', 'pending_merchant_fulfillment'].includes(row.status) ? 'primary' : 'default'}
          size="small"
          onClick={() => {
            form.resetFields();
            setSelected(row);
          }}
        >
          {['pending_merchant_review', 'pending_merchant_fulfillment'].includes(row.status) ? '处理' : '查看'}
        </Button>
      )
    }
  ];

  return (
    <div className="member-management">
      <Card>
        <h2>售后管理</h2>
        <Paragraph type="secondary">
          审核团队提交的退货或换货申请，报价经团队管理员确认后，由用户寄回商品。
        </Paragraph>
        <Table
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 900 }}
          pagination={{ pageSize: 15 }}
        />
      </Card>
      <Modal
        title={selected ? `${itemLabel(selected.originalItem)} · 售后详情` : '售后详情'}
        open={Boolean(selected)}
        onCancel={() => setSelected(null)}
        width={720}
        footer={
          selected?.status === 'pending_merchant_review'
            ? (
                <Space>
                  <Button onClick={() => setSelected(null)}>取消</Button>
                  <Button danger loading={submitting} onClick={() => void review(false)}>拒绝</Button>
                  <Button type="primary" loading={submitting} onClick={() => void review(true)}>
                    同意并提交报价
                  </Button>
                </Space>
              )
            : selected?.status === 'pending_merchant_fulfillment'
              ? (
                  <Space>
                    <Button onClick={() => setSelected(null)}>取消</Button>
                    <Button type="primary" loading={submitting} onClick={() => void fulfill()}>
                      {selected.type === 'return' ? '确认已退款' : '确认已发货'}
                    </Button>
                  </Space>
                )
              : <Button onClick={() => setSelected(null)}>关闭</Button>
        }
      >
        {selected && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="状态" span={2}>
                <Tag color={STATUS_COLOR[selected.status]}>
                  {AFTER_SALES_STATUS_TEXT[selected.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="申请人">{selected.applicantName}</Descriptions.Item>
              <Descriptions.Item label="团队订单类型">{selected.category}</Descriptions.Item>
              <Descriptions.Item label="售后类型">
                {selected.type === 'return' ? '退货退款' : '换货'}
              </Descriptions.Item>
              <Descriptions.Item label="数量">{selected.requestedQuantity}</Descriptions.Item>
              <Descriptions.Item label="原因">{selected.reason}</Descriptions.Item>
              <Descriptions.Item label="原商品售后价值">
                ¥{Number(selected.originalReturnValue || 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="问题说明" span={2}>{selected.description || '—'}</Descriptions.Item>
              <Descriptions.Item label="是否拆封">{selected.opened ? '是' : '否'}</Descriptions.Item>
              <Descriptions.Item label="影响二次销售">{selected.affectsResale ? '是' : '否'}</Descriptions.Item>
              <Descriptions.Item label="退货物流">{selected.returnTrackingNumber || '—'}</Descriptions.Item>
              <Descriptions.Item label="退款凭证">{selected.refundReference || '—'}</Descriptions.Item>
              <Descriptions.Item label="换货物流" span={2}>
                {selected.merchantOutboundTrackingNumber || '—'}
              </Descriptions.Item>
            </Descriptions>
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
              {selected.status === 'pending_merchant_review' && (
                <>
                  {selected.type === 'return' ? (
                    <Form.Item
                      name="merchantConfirmedAmount"
                      label="确认退款金额"
                      initialValue={selected.expectedRefundAmount ?? selected.originalReturnValue}
                      rules={[{ required: true, message: '请填写退款金额' }]}
                    >
                      <InputNumber min={0} precision={2} prefix="¥" style={{ width: '100%' }} />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="replacementQuote"
                      label="换货商品报价"
                      rules={[{ required: true, message: '请填写换货报价' }]}
                    >
                      <InputNumber min={0} precision={2} prefix="¥" style={{ width: '100%' }} />
                    </Form.Item>
                  )}
                  <Form.Item
                    name="note"
                    label="审核说明（拒绝时必填）"
                  >
                    <Input.TextArea rows={3} />
                  </Form.Item>
                </>
              )}
              {selected.status === 'pending_merchant_fulfillment' && (
                selected.type === 'return' ? (
                  <Form.Item name="refundReference" label="退款凭证/流水号">
                    <Input />
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="trackingNumber"
                    label="换货发货物流单号"
                    rules={[{ required: true, message: '请填写物流单号' }]}
                  >
                    <Input />
                  </Form.Item>
                )
              )}
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default MerchantAfterSales;
